<script
  setup
  lang="ts"
  generic="T extends TableStats<K>, K extends Record<string, string | number>"
>
import { CurveType } from '@unovis/ts'
import { computed, MaybeRef, unref } from 'vue'
import { TableStats } from '../../types/stats'
import { Badge } from '../ui/badge'
import { BarChart } from '../ui/chart-bar'
import { LineChart } from '../ui/chart-line'
import CustomChartTooltip from '../ui/chart/CustomChartTooltip.vue'
import { toast } from '../ui/toast'

type DataOptions = {
  key: keyof T
  name: string
  color?: string
  icon?: string
  formatter?: (value: number) => number
  accumulate?: boolean
}

const props = defineProps<{
  dataOptions: DataOptions[]
  data: MaybeRef<T[]>
  isMoney?: boolean
  isBar?: boolean
}>()

const dataFormatted = computed(() => {
  // Format date and rename the keys to the names provided in dataOptions
  const data = unref(props.data)
  if (!data) return []

  const formattedDataResult: Record<string, string | number>[] = []

  const sortedData = [...data].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  )

  for (const item of sortedData ?? []) {
    const date = new Date(item.date).toLocaleDateString('no-NO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })

    const formattedData: Record<string, number | string> = {
      date,
    }
    props.dataOptions.forEach((option) => {
      let currentValue = option.formatter
        ? option.formatter(item[option.key] as number)
        : item[option.key]

      if (option.accumulate) {
        if (!isFinite(currentValue as number)) {
          currentValue = 0
          toast({
            title: 'Invalid value',
            description: `The value for ${option.name} is not a finite number. It will be set to 0.`,
            variant: 'destructive',
          })
        }

        const lastValue = formattedDataResult[formattedDataResult.length - 1]?.[option.name] || 0
        currentValue = ((currentValue ?? 0) as number) + (lastValue as number)
      }

      formattedData[option.name] = currentValue
    })

    formattedDataResult.push(formattedData)
  }

  return formattedDataResult ?? []
})

const colors = computed(() => props.dataOptions.map((option) => option.color || '#6366F1'))

function valueFormatter(tick: number | Date) {
  if (props.isMoney && typeof tick === 'number')
    return new Intl.NumberFormat('no-NO', { style: 'currency', currency: 'NOK' }).format(
      tick as number
    )

  return typeof tick === 'number' ? `${new Intl.NumberFormat('no-NO').format(tick).toString()}` : ''
}
</script>
<template>
  <!-- <DataStateDisplay v-bind="dataState"> -->
  <!-- <div class="w-full max-h-full h-full flex gap-2"> -->
  <div>
    <LineChart
      v-if="!props.isBar"
      :data="dataFormatted"
      index="date"
      :categories="dataOptions.map((d) => d.name)"
      :curve-type="CurveType.Linear"
      :y-formatter="valueFormatter"
      :colors="colors"
      class="max-h-full h-full"
      :custom-tooltip="CustomChartTooltip"
      :show-legend="false"
    />
    <BarChart
      v-else
      :data="dataFormatted"
      index="date"
      :categories="dataOptions.map((d) => d.name)"
      :y-formatter="valueFormatter"
      :colors="colors"
      :show-legend="false"
      class="max-h-full h-full"
      :custom-tooltip="CustomChartTooltip"
    />
    <div class="flex gap-2 mt-2 flex-wrap">
      <Badge
        v-for="option in dataOptions"
        :key="option.name"
        :color="option.color || '#6366F1'"
        class="rounded-full"
        variant="outline"
      >
        <div
          class="h-2 w-2 mr-2 rounded-full"
          :style="{ backgroundColor: option.color || '#6366F1' }"
        ></div>
        {{ option.name }}
      </Badge>
    </div>
  </div>
  <!-- <div class="flex flex-col gap-2">
      <slot :="{ data }" />
    </div>
  </div> -->
  <!-- </DataStateDisplay> -->
</template>
